import React, { useCallback, useState } from 'react';
import * as Yup from 'yup';
import { connect } from 'react-redux';
import { useHistory } from 'react-router-dom';
import { ButtonsContainer, Container, FilesContainer, SelectOptContainer } from './styles';
import { FiSave } from 'react-icons/fi';
import { Breadcrumb, Button, Divider, Input, message, Select, Typography } from 'antd';
import { MaskedInput } from 'antd-mask-input';

import brStates from '../../../../constants/states';
import Meta from '../../../../components/Meta';
import Form from '../../../../components/Form';
import { validatePhone, validateZipCode } from '../../../../helpers/validators';
import CastersApi from '../../../../services/sdks/caster';
import TextArea from 'antd/lib/input/TextArea';
import FilesUploader from '../../../../components/FilesUploader';

const INITIAL_DATA = {
	name: '',
	surname: '',
	sex: undefined,
	email: '',
	bio: '',
	phone: '(__) _ ____-____',
	zipCode: '_____-___',
	state: undefined,
	city: '',
	mic: '',
	soundcard: '',
	password: '',
	passwordConfirmation: '',
};

const INITIAL_ERRORS = {
	...INITIAL_DATA,
	phone: false,
	zipCode: false,
};

export const createCasterValidationShape = {
	name: Yup.string().required(),
	surname: Yup.string().required(),
	sex: Yup.string().required(),
	bio: Yup.string().required(),
	email: Yup.string().required(),
	userId: Yup.string().nullable(),
	state: Yup.string().required(),
	city: Yup.string().required(),
	mic: Yup.string().required(),
	soundcard: Yup.string().required(),
	zipCode: Yup.string()
		.required()
		.test('zipCode', (v) => validateZipCode(v).isValid)
		.transform((v) => validateZipCode(v).value),
	phone: Yup.string()
		.required()
		.test('phone', (v) => validatePhone(v).isValid)
		.transform((v) => validatePhone(v).value),
	password: Yup.string().required(),
	passwordConfirmation: Yup.string()
		.required()
		.oneOf([Yup.ref('password')]),
};

const createCasterValidationSchema = Yup.object().shape(createCasterValidationShape);

const CreateCaster = ({ user }) => {
	const history = useHistory();
	const [formData, setFormData] = useState(INITIAL_DATA);
	const [errors, setErrors] = useState(INITIAL_ERRORS);
	const [isLoading, setIsLoading] = useState(false);
	const [isEmailTaken, setIsEmailTaken] = useState(false);
	const [isValidatingDemo, setIsValidatingDemo] = useState(false);

	const [files, setFiles] = useState({
		audioDemo: null,
		profilePic: null,
	});

	const handleSelectFile = useCallback((key, [file]) => {
		if (!file || file.status.code !== 'VALID') {
			return setFiles((prev) => ({ ...prev, [key]: null }));
		}

		setFiles((prev) => ({ ...prev, [key]: file.data }));
	}, []);

	const validateFiles = useCallback(() => {
		if (!files.audioDemo && !files.profilePic) {
			message.error('Selecione a imagem de perfil e um audio demo');
			return false;
		}
		if (!files.profilePic) {
			message.error('Selecione a imagem de perfil');
			return false;
		}
		if (!files.audioDemo) {
			message.error('Selecione um audio demo');
			return false;
		}
		return true;
	}, [files]);

	const mountPayload = useCallback(
		(formFields) => {
			const payload = new FormData();
			payload.append('userId', user._id);

			for (const key in files) {
				payload.append(key, files[key]);
			}

			for (const key in formFields) {
				payload.append(key, formFields[key]);
			}

			return payload;
		},
		[files, user]
	);

	const handleCreateCaster = useCallback(async () => {
		setErrors(INITIAL_ERRORS);

		try {
			const formValues = await createCasterValidationSchema.validate(formData, {
				abortEarly: false,
			});

			if (validateFiles()) {
				setIsLoading(true);
				const payload = mountPayload(formValues);
				await CastersApi.store(payload);
				message.success('Locutor cadastrado com sucesso', 3, () => history.push(`/commom/casters`));
			}
		} catch (error) {
			setIsLoading(false);

			if (error instanceof Yup.ValidationError) {
				return setErrors(
					Object.values(error.inner).reduce((err, { path }) => ({ ...err, [path]: true }), {})
				);
			}

			if (error.status === 409) {
				setErrors((prev) => ({ ...prev, email: true }));
				return setIsEmailTaken(true);
			}

			message.error('Houve um erro, tente novamente');
		}
	}, [formData, history, validateFiles, mountPayload]);

	const handleChange = useCallback((e) => {
		e?.persist && e.persist();
		setFormData((prev) => ({
			...prev,
			[e.target.name]: e.target.value,
		}));
	}, []);

	return (
		<>
			<Meta title='Novo Locutor' />

			<Container>
				<Breadcrumb
					style={{ marginBottom: 12 }}
					separator='>'
					routes={[
						{ breadcrumbName: 'INÍCIO' },
						{ breadcrumbName: 'MEUS LOCUTORES' },
						{ breadcrumbName: 'NOVO LOCUTOR' },
					]}
				/>

				<header>
					<Typography.Title level={2}>Cadastrar Locutor</Typography.Title>
				</header>

				<Form.Container layout='1fr 1fr 1fr 1fr 1fr 1fr'>
					<Form.Item size={3} label='Nome' error={errors?.name}>
						<Input
							name='name'
							value={formData.name}
							size='large'
							placeholder='Nome do locutor'
							onChange={handleChange}
						/>
					</Form.Item>
					<Form.Item size={3} label='Sobrenome' error={errors?.surname}>
						<Input
							name='surname'
							value={formData.surname}
							size='large'
							placeholder='Sobrenome do locutor'
							onChange={handleChange}
						/>
					</Form.Item>
					<Form.Item
						size={2}
						label='Email'
						error={errors?.email}
						errorMessage={isEmailTaken ? 'Email já está em uso' : 'Email inválido'}>
						<Input
							name='email'
							value={formData.email}
							size='large'
							placeholder='Email do locutor'
							onChange={handleChange}
						/>
					</Form.Item>
					<Form.Item size={2} label='Sexo' error={errors?.sex}>
						<Select
							size='large'
							value={formData.sex}
							onChange={(value) => handleChange({ target: { name: 'sex', value } })}
							placeholder='Selecione o sexo do locutor'>
							<Select.Option value='MALE'>Masculino</Select.Option>
							<Select.Option value='FEMALE'>Feminino</Select.Option>
						</Select>
					</Form.Item>
					<Form.Item
						size={2}
						label='Telefone'
						error={errors?.phone}
						errorMessage='Telefone inválido'>
						<MaskedInput
							name='phone'
							value={formData.phone}
							size='large'
							autoComplete='off'
							mask='(00) 0 0000-0000'
							placeholder={INITIAL_DATA.phone}
							onChange={handleChange}
						/>
					</Form.Item>

					<Form.Item size={2} label='CEP' error={errors?.zipCode} errorMessage='CEP inválido'>
						<MaskedInput
							name='zipCode'
							value={formData.zipCode}
							size='large'
							autoComplete='off'
							mask='00000-000'
							placeholder={INITIAL_DATA.zipCode}
							onChange={handleChange}
						/>
					</Form.Item>
					<Form.Item size={2} label='Estado' error={errors?.state}>
						<Select
							size='large'
							value={formData.state}
							onChange={(value) => handleChange({ target: { name: 'state', value } })}
							placeholder='Selecione o estado'>
							{brStates.map(({ label, value }) => (
								<Select.Option key={value} value={value}>
									<SelectOptContainer>
										<strong>{value}</strong>
										<small>{label}</small>
									</SelectOptContainer>
								</Select.Option>
							))}
						</Select>
					</Form.Item>
					<Form.Item size={2} label='Cidade' error={errors?.city}>
						<Input
							name='city'
							value={formData.city}
							size='large'
							placeholder='Cidade do locutor'
							onChange={handleChange}
						/>
					</Form.Item>

					<Form.Item size={6} label='Descrição do locutor' error={errors?.bio}>
						<TextArea
							name='bio'
							rows={3}
							value={formData.bio}
							size='large'
							placeholder='Descreva o locutor, com suas caracteristicas principais, afinidades, entonação, especialidades e etc...'
							onChange={handleChange}
						/>
					</Form.Item>

					<Form.Item size={3} label='Microfone' error={errors?.mic}>
						<Input
							name='mic'
							value={formData.mic}
							size='large'
							placeholder='Modelo do microfone'
							onChange={handleChange}
						/>
					</Form.Item>
					<Form.Item size={3} label='Placa de som' error={errors?.soundcard}>
						<Input
							name='soundcard'
							value={formData.soundcard}
							size='large'
							placeholder='Modelo da placa de som'
							onChange={handleChange}
						/>
					</Form.Item>

					<Form.Item error={errors.password} size={3} label='Senha'>
						<Input.Password
							name='password'
							value={formData.password}
							onChange={handleChange}
							size='large'
							placeholder='Senha'
						/>
					</Form.Item>
					<Form.Item
						error={errors.passwordConfirmation}
						errorMessage='Senhas não são iguais'
						size={3}
						label='Confirmar Senha'>
						<Input.Password
							name='passwordConfirmation'
							value={formData.passwordConfirmation}
							onChange={handleChange}
							size='large'
							placeholder='Confirmar senha'
						/>
					</Form.Item>
				</Form.Container>

				<Divider />

				<FilesContainer>
					<div>
						<Typography.Title level={4}>Foto de Perfil</Typography.Title>
						<FilesUploader
							isDisabled={isValidatingDemo}
							multiple={false}
							title='Selecione uma imagem de perfil'
							onChange={(files) => handleSelectFile('profilePic', files)}
							validTypes={['.jpg', '.jpeg', '.png']}
							onStartValidation={() => setIsValidatingDemo(true)}
							onEndValidation={() => setIsValidatingDemo(false)}
						/>
					</div>
					<div>
						<Typography.Title level={4}>Audio demo</Typography.Title>
						<FilesUploader
							isDisabled={isValidatingDemo}
							multiple={false}
							title='Selecione uma demo de voz'
							onChange={(files) => handleSelectFile('audioDemo', files)}
							validTypes={['.mp3', '.wav']}
							onStartValidation={() => setIsValidatingDemo(true)}
							onEndValidation={() => setIsValidatingDemo(false)}
						/>
					</div>
				</FilesContainer>

				<ButtonsContainer>
					<Button type='primary' size='large' onClick={handleCreateCaster} loading={isLoading}>
						<FiSave />
						Cadastrar Locutor
					</Button>
				</ButtonsContainer>
			</Container>
		</>
	);
};

export default connect(({ user }) => ({ user }))(CreateCaster);
